import { useEffect, useMemo, useState } from 'react';
import { fetchFileMonitoringData } from '../services/fileMonitoringService';
import { fetchSupplierNames } from '../services/supplierService';
import MultiSelect from '../components/MultiSelect';
import * as XLSX from 'xlsx';
import './Suppliers.css';

const DEFAULT_PAGE_SIZE = 50;

function fmtDate(d) {
  if (!d) return '\u2014';
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(dt.getDate()).padStart(2, '0')} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
}

const TH = {
  padding: '10px 14px',
  fontWeight: 600,
  fontSize: 12,
  color: 'var(--navy)',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
  borderBottom: '2px solid #e3e8ef',
};

export default function FileMonitoring() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [supplierOpts, setSupplierOpts] = useState(null);

  const [filters, setFilters] = useState({ supplier: [] });
  const [sortCol, setSortCol] = useState('upload_date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selected, setSelected] = useState(new Set());
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchFileMonitoringData()
      .then(setRows)
      .catch(err => setError(err.response?.data?.error || err.message));
    fetchSupplierNames()
      .then(setSupplierOpts)
      .catch(() => setSupplierOpts([]));
  }, []);

  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 3000); return () => clearTimeout(t); } }, [toast]);

  // Filter
  const filtered = useMemo(() => {
    if (!rows) return [];
    return rows.filter(r => {
      if (filters.supplier.length > 0 && !filters.supplier.includes(r.supplier_name)) return false;
      return true;
    });
  }, [rows, filters]);

  // Sort
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av = a[sortCol], bv = b[sortCol];
      if (av == null) av = '';
      if (bv == null) bv = '';
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortCol, sortDir]);

  // Pagination
  const effectivePageSize = Math.max(1, pageSize || DEFAULT_PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(sorted.length / effectivePageSize));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * effectivePageSize;
  const pageEnd = Math.min(pageStart + effectivePageSize, sorted.length);
  const pageRows = sorted.slice(pageStart, pageEnd);

  useEffect(() => { setPage(1); }, [filters, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir(col === 'supplier_name' || col === 'file_name' ? 'asc' : 'desc'); }
  };
  const arrow = (col) => sortCol === col ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : '';

  // Selection — spans all pages via sorted array identity
  const allPageIds = useMemo(() => new Set(pageRows.map((_, i) => pageStart + i)), [pageRows, pageStart]);
  const allSelected = pageRows.length > 0 && pageRows.every((_, i) => selected.has(pageStart + i));

  const toggleOne = (globalIdx) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(globalIdx)) next.delete(globalIdx); else next.add(globalIdx);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) {
        allPageIds.forEach(id => next.delete(id));
      } else {
        allPageIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  // Download
  const handleDownload = () => {
    if (selected.size === 0) {
      setToast({ type: 'warn', msg: 'Please select at least one row to download.' });
      return;
    }
    const exportRows = [...selected].sort((a, b) => a - b).map(i => sorted[i]).filter(Boolean);
    if (exportRows.length === 0) return;

    const data = exportRows.map(r => ({
      'Supplier Name': r.supplier_name || '',
      'File Name': r.file_name || '',
      'Reference Date': fmtDate(r.reference_date),
      'Upload Date': fmtDate(r.upload_date),
      'Uploaded By': r.uploaded_by || '',
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'File Monitoring');
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `file_monitoring_${stamp}.xlsx`);
  };

  return (
    <div className="sup-page">
      {/* Header */}
      <div className="mb-3">
        <h2 style={{ fontWeight: 700, fontSize: 20, color: 'var(--navy)', margin: 0 }}>File Monitoring</h2>
        <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>FocusOne &middot; Supplier Files</div>
      </div>

      {/* Toolbar */}
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <div style={{ minWidth: 220 }}>
          <MultiSelect
            label="Supplier"
            options={supplierOpts || []}
            values={filters.supplier}
            onChange={v => setFilters(f => ({ ...f, supplier: v }))}
            placeholder="All Suppliers"
          />
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button
            className="btn btn-sm"
            style={{ background: 'var(--teal)', color: 'white', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={handleDownload}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
            Download
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ marginBottom: 12, padding: '8px 14px', borderRadius: 8, fontSize: 13, background: toast.type === 'warn' ? '#fff3cd' : '#d4edda', color: toast.type === 'warn' ? '#856404' : '#155724', border: `1px solid ${toast.type === 'warn' ? '#ffeeba' : '#c3e6cb'}` }}>
          {toast.msg}
        </div>
      )}

      {/* Table */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e3e8ef', overflow: 'hidden' }}>
        {error ? (
          <div className="text-center py-5" style={{ color: 'var(--red)' }}>
            <p style={{ margin: 0 }}>Failed to load file data</p>
            <p style={{ fontSize: 12, margin: '4px 0 0' }}>{error}</p>
          </div>
        ) : !rows ? (
          <div className="text-center py-5" style={{ color: 'var(--t3)' }}>Loading files...</div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-5" style={{ color: 'var(--t3)' }}>
            <p style={{ margin: 0, fontSize: 14 }}>No files found</p>
            <p style={{ fontSize: 12, margin: '4px 0 0' }}>
              {filters.supplier.length > 0 ? 'Try clearing the supplier filter.' : 'No supplier files are recorded yet.'}
            </p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ ...TH, cursor: 'default', width: 36 }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                    </th>
                    <th style={TH} onClick={() => handleSort('supplier_name')}>Supplier Name{arrow('supplier_name')}</th>
                    <th style={TH} onClick={() => handleSort('file_name')}>File Name{arrow('file_name')}</th>
                    <th style={TH} onClick={() => handleSort('reference_date')}>Reference Date{arrow('reference_date')}</th>
                    <th style={TH} onClick={() => handleSort('upload_date')}>Upload Date{arrow('upload_date')}</th>
                    <th style={TH} onClick={() => handleSort('uploaded_by')}>Uploaded By{arrow('uploaded_by')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r, i) => {
                    const globalIdx = pageStart + i;
                    const isSel = selected.has(globalIdx);
                    return (
                      <tr key={globalIdx} className={isSel ? 'table-active' : ''}>
                        <td style={{ padding: '8px 14px' }}>
                          <input type="checkbox" checked={isSel} onChange={() => toggleOne(globalIdx)} />
                        </td>
                        <td style={{ padding: '8px 14px', fontWeight: 500 }}>{r.supplier_name}</td>
                        <td style={{ padding: '8px 14px', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.file_name}>{r.file_name}</td>
                        <td style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}>{fmtDate(r.reference_date)}</td>
                        <td style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}>{fmtDate(r.upload_date)}</td>
                        <td style={{ padding: '8px 14px' }}>{r.uploaded_by || '\u2014'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination — matches SupplierProducts exactly */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--card)', borderTop: '1px solid #eef3f7', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, flexWrap: 'nowrap' }}>
              <button
                type="button"
                onClick={() => { if (pageSize < sorted.length) { setPageSize(sorted.length); } else { setPageSize(DEFAULT_PAGE_SIZE); } setPage(1); }}
                style={{ fontSize: 12, color: 'var(--teal)', border: '1px solid var(--teal)', background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {pageSize < sorted.length ? 'Show All' : `Show ${DEFAULT_PAGE_SIZE}`}
              </button>
              <nav aria-label="pagination" style={{ flexShrink: 0, display: 'flex', gap: 4, alignItems: 'center' }}>
                <button type="button" disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ fontSize: 12, color: safePage === 1 ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === 1 ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === 1 ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Previous</button>
                {(() => {
                  const win = 5;
                  let s = Math.max(1, safePage - Math.floor(win / 2));
                  let e = Math.min(pageCount, s + win - 1);
                  s = Math.max(1, e - win + 1);
                  const nums = [];
                  for (let i = s; i <= e; i++) nums.push(i);
                  return nums.map(n => (
                    <button key={n} type="button" onClick={() => setPage(n)} style={{ fontSize: 12, color: safePage === n ? 'white' : 'var(--teal)', border: '1px solid var(--teal)', background: safePage === n ? 'var(--teal)' : 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: safePage === n ? 600 : 400 }}>{n}</button>
                  ));
                })()}
                <button type="button" disabled={safePage === pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))} style={{ fontSize: 12, color: safePage === pageCount ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === pageCount ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === pageCount ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Next</button>
              </nav>
              <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--t3)', whiteSpace: 'nowrap' }}>
                Showing {sorted.length === 0 ? 0 : pageStart + 1}–{pageEnd} of {sorted.length.toLocaleString('en-US')} {sorted.length === 1 ? 'file' : 'files'}
                {selected.size > 0 && <span style={{ marginLeft: 8, color: 'var(--teal)', fontWeight: 600 }}>({selected.size} selected)</span>}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
